import { useState, useEffect, useContext } from 'react'
import Section from './_Section'
import createGradientElement from '../../helpers/markup'
import {
  addBgImageClass,
  lightOrDark,
  normalizeHex,
  transformPaddingToTailwind,
  getSectionTheme,
} from '../../helpers/style'
import ButtonEl from '../helpers/ctas/Button'
import { ArrowRightIcon, MapPinIcon, LockClosedIcon } from '@heroicons/react/24/solid'
import { CalendarIcon } from '@heroicons/react/24/outline'
import HeroTitle from '../helpers/CardListingHeroTitle'
import TitleBlock from '../helpers/TitleBlock'
import Picture from '../helpers/Picture'
import { MarkupContext } from '../../providers/Markup'
import MetaBadge from '../helpers/MetaBadge'
import { formatDate } from '../../helpers/util'
import { appendListingParams } from '../../helpers/listingPageUrl'
import { dataLayerPush } from '../../helpers/thirdparty'

const stripHtml = (html) =>
  html
    ? html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim()
    : ''

const CardMetaInfo = ({ location, date, className = '' }) => {
  // If neither exist, don't render the wrapper at all
  if (!location && !date) return null

  return (
    <div className={`flex flex-col gap-3 mt-4 ${className}`}>
      {/* Location Row */}
      {location && (
        <div className="flex items-center gap-3">
          <MapPinIcon className="w-4 h-4 text-(--color-primary-text) shrink-0" aria-hidden="true" />
          <span className="text-(--sb-common-base-body-text-color) text-xs font-normal">{location}</span>
        </div>
      )}

      {/* Date Row */}
      {date && (
        <div className="flex items-center gap-3">
          <CalendarIcon className="w-4 h-4 text-(--color-primary-text) shrink-0" aria-hidden="true" />
          <span className="text-(--sb-common-base-body-text-color) text-xs font-normal">{formatDate(date)}</span>
        </div>
      )}
    </div>
  )
}

function CardListDynamic(props) {
  const { createMarkup } = useContext(MarkupContext)
  let cardItems = (props.listingContents || []).filter((cardItem) => !cardItem.hideInListing)
  const componentPadding = transformPaddingToTailwind(props.componentPadding)

  // "View more" navigates to the editor-provided CTA link with the listing filter params
  // appended; falls back to in-place expansion when no CTA link is configured.
  const listingUrl = appendListingParams(
    props.ctaLinkItem?.[0]?.link?.url,
    props.filteredContentType,
    props.filteredByTopic,
    props.filteredByTrainingFormat,
  )

  const initialNumber = props.initialNoOfCards || 3
  const [viewMore, setViewMore] = useState(cardItems.length <= initialNumber)
  const [alignment, setAlignment] = useState('center')
  const [textAlignment, setTextAlignment] = useState({
    flex: 'center',
    align: 'center',
  })

  const [cardType, setCardType] = useState('none')

  // Resolve card variant (mirrors CardListCurated's split logic):
  //   undefined / null / 'Icon'    → 'icon'       (renders like CardListIcon)
  //   'Hero image'                 → 'hero'        (renders like CardListHeroImage)
  //   'News'                       → 'news'        (image-less news card, mirrors CardListCurated's News variant)
  //   'Background image'           → 'background'  (renders like CardListBGImage)
  const rawVariant = props.cardVariant
  const variant =
    !rawVariant || rawVariant === 'Icon'
      ? 'icon'
      : rawVariant === 'Hero image'
        ? 'hero'
        : rawVariant === 'News'
          ? 'news'
          : 'background'

  const showImage = props.showImage !== false

  // News cards can be locked; resolve member status so the CTA label matches CardListCurated
  // ('Sign in to continue reading' for signed-out users, 'Continue reading' for members).
  const [isMember, setIsMember] = useState(null)
  useEffect(() => {
    if (variant !== 'news') return
    const hasLockedCards = (props.listingContents || []).some((c) => c.metaBadgeData?.isLocked)
    if (!hasLockedCards) return

    fetch('/api/content/member-status', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { isLoggedIn: false }))
      .then((data) => setIsMember(!!data.isLoggedIn))
      .catch(() => setIsMember(false))
  }, []) && rawVariant !== 'News'

  const [gradientColor, setGradientColor] = useState('#fff')

  useEffect(() => {
    setTextAlignment((prevState) => {
      const newState = { ...prevState }
      const alignment = props.contentAlignment?.toLowerCase() ?? 'center'
      if (newState.align !== props.contentAlignment?.toLowerCase()) {
        newState.flex = alignment === 'center' ? 'center' : 'around'
        newState.align = alignment === 'center' ? 'center' : 'left'
      }
      setAlignment(alignment)
      return newState
    })
    const fgColor = props.imageGradientColour?.replace('#', '') || ''
    setGradientColor((prevState) => (fgColor ? `#${fgColor}` : prevState))

    setCardType((pre) => {
      // None ---> none
      // Event ---> event
      // Training and Development ---> training
      // Nothing selcted ---> none

      if (!props.cardType) return 'none'

      return props.cardType?.split(' ')[0].toLowerCase()
    })
  }, [])

  if (!viewMore) {
    cardItems = cardItems.slice(0, initialNumber)
  }

  // Append the listing filter params to each editor-provided CTA link URL.
  const ctaLinkItems = (props.ctaLinkItem || [])
    .filter((ctaLinkItem) => ctaLinkItem.link?.url)
    .map((ctaLinkItem) => ({
      ...ctaLinkItem,
      link: {
        ...ctaLinkItem.link,
        url: appendListingParams(
          ctaLinkItem.link.url,
          props.filteredContentType,
          props.filteredByTopic,
          props.filteredByTrainingFormat,
        ),
      },
    }))

  const backgroundColor = normalizeHex(props.cardBackgroundColour)
  const lightOrDarkValue = lightOrDark(backgroundColor || normalizeHex(props.backgroundColor) || 'transparent')

  // Shared grid-centering classes: 3-up layout on md+, auto-centres incomplete last rows
  const gridCenteringClasses = ``

  return (
    <Section
      type="cardListDynamic"
      sectionTitle={false}
      innerClass={`px-6! lg:px-8! text-${textAlignment.align} flex ${componentPadding}`}
      {...props}
    >
      <div className="w-full">
        <div className="flex flex-row items-center justify-between gap-6 md:gap-8 mb-8">
          <div className="flex-1 [&_*]:!my-0">
            {(props.title || props.description) && (
              <TitleBlock
                {...props}
                headingClass={
                  'text-(--color-primary-text) !font-light' +
                  ' [&_[data-type="section-title"]]:text-5xl' +
                  ' [&_[data-type="section-title"]]:leading-none' +
                  ' [&_[data-type="section-title"]]:tracking-normal'
                }
              />
            )}
          </div>
          {/* Buttons Container */}
          {ctaLinkItems && ctaLinkItems.length > 0 && (
            <div className="flex flex-wrap gap-4 shrink-0 items-center">
              {ctaLinkItems.map((ctaLinkItem, i) => {
                const resolvedLinkType = ctaLinkItem?.linkType?.toLowerCase() || 'text'
                return (
                  <ButtonEl
                    key={i}
                    link={ctaLinkItem?.link}
                    label={
                      <>
                        <span className="md:hidden">View all</span>
                        <span className="hidden md:inline">{ctaLinkItem?.link?.name}</span>
                      </>
                    }
                    icon={ctaLinkItem?.linkIconClass}
                    position={ctaLinkItem?.linkPosition}
                    theme={resolvedLinkType}
                    className={`${!props.backgroundColor && resolvedLinkType === 'text' ? 'text-(--color-primary-text) !font-normal underline   decoration-1 hover:no-underline' : ''}`}
                  />
                )
              })}
            </div>
          )}
        </div>
        <hr className="w-full mt-8 border-t border-(--color-border-tertiary)" />

        {cardItems.length != 0 && (
          <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-stretch ${props.title?.length || props.description?.length ? 'pt-10' : 'pt-0'} gap-8`}
          >
            {cardItems.map((cardItem, i) => {
              const tags = (cardItem.tagsConfig && JSON.parse(cardItem.tagsConfig).tags) || []
              const cardCtaItems = cardItem.ctaLinkItem || []

              let borderColor = ''
              const secondaryTextColorClass = 'text-(--sb-meta-badge-primary-text-color)'
              switch (cardType) {
                case 'event':
                  borderColor = 'border-t-8 border-t-(--sb-meta-badge-event-theme)'

                  break
                case 'training':
                  borderColor = 'border-t-8 border-t-(--sb-meta-badge-t-and-d-theme)'
                  break
                default:
                  borderColor = ''
                  break
              }

              const isEventCard = cardType === 'event' || cardType === 'training'
              const firstCategoryTag = isEventCard
                ? cardItem.eventCategoryTag || cardItem.trainingFormat || cardItem?.topics?.[0]
                : cardItem?.topics?.[0]
              const badgeIsLocked = cardItem?.metaBadgeData?.isLocked ?? false

              const handleCardClick = (e) => {
                dataLayerPush(
                  {
                    event: 'event_click',
                    event_title: cardItem.listingTitle,
                    event_date: cardItem.dateTimeFormatted,
                    event_location: cardItem.location ?? null,
                    member_fee: cardItem.memberFee ?? null,
                    nonmember_fee: cardItem.nonMemberFee ?? null,
                    event_delivery: cardItem.eventDelivery ?? null,
                    event_state: cardItem?.metaBadgeData?.categoryTags?.[0] ?? null,
                    event_type: props.cardType,
                  },
                  e.currentTarget,
                )
              }

              // ── Icon Variant ─────────────────────────────────────────────────────
              // Card container stays a <div>; HeroTitle handles title-level linking
              // via listingLink or ctaLinkItem[0] (avoids invalid nested <a> HTML).
              if (variant === 'icon') {
                return (
                  <div
                    key={i}
                    className={`relative w-full h-full flex flex-col ${gridCenteringClasses} rounded-e-3xl rounded-bl-3xl border-1 border-(--color-border-tertiary) ${borderColor} p-6`}
                  >
                    {/* Event/training cards show the first listing category tag as a section
                        label (e.g. "WORKFORCE"). Other cards show the listing image. */}
                    {isEventCard ? (
                      <div className="h-[80px] w-full mb-4 shrink-0 flex items-center justify-center">
                        {cardItem.metaBadgeData?.categoryTags?.[0] && (
                          <p className="text-base font-bold tracking-normal leading-6 text-center !text-[#414042]">
                            {cardItem.metaBadgeData.categoryTags[0]}
                          </p>
                        )}
                      </div>
                    ) : (
                      showImage && (
                        <div className="h-[80px] w-full mb-4 shrink-0 flex justify-start">
                          {(cardItem.listingDesktopImage || cardItem.listingMobileImage) && (
                            <div className="w-16 h-16">
                              <Picture
                                desktopImage={cardItem.listingDesktopImage}
                                mobileImage={cardItem.listingMobileImage}
                                className="aspect-square w-full h-full object-cover"
                              />
                            </div>
                          )}
                        </div>
                      )
                    )}

                    <div className="h-[160px] shrink-0 mb-4 overflow-hidden">
                      {cardItem.listingTitle && (
                        <HeroTitle
                          type="dynamic"
                          cardItem={cardItem}
                          idx={i}
                          className="text-2xl leading-8 text-(--color-primary-text) line-clamp-5"
                          onCardClick={handleCardClick}
                        />
                      )}
                    </div>

                    <div className="h-[36px] shrink-0 mb-4 flex items-start">
                      {(firstCategoryTag ||
                        (isEventCard ? cardItem.deliveryMode : cardItem?.metaBadgeData?.readTime)) && (
                        <MetaBadge
                          primaryText={firstCategoryTag}
                          secondaryText={isEventCard ? cardItem.deliveryMode : cardItem?.metaBadgeData?.readTime}
                          tagType={cardType}
                          isLocked={badgeIsLocked}
                          icon={badgeIsLocked ? <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" /> : null}
                          secondaryTextColorClass={secondaryTextColorClass}
                        />
                      )}
                    </div>

                    <div className="h-[80px] shrink-0 mb-4 flex items-start">
                      <CardMetaInfo location={cardItem.location} date={cardItem.dateTimeFormatted} />
                    </div>

                    <div className="flex-1 overflow-hidden">
                      {cardItem.listingDescription && (
                        <div
                          className="font-highlight text-sm font-normal leading-5 tracking-normal text-[#414042] [&_*:not(a)]:text-[#414042] [&_a[href]:not(.cta)]:text-nds-blue [&_a[href]:not(.cta)]:underline line-clamp-4"
                          data-rte="true"
                          dangerouslySetInnerHTML={createMarkup(cardItem.listingDescription)}
                        />
                      )}
                    </div>

                    <div className="shrink-0 mt-6 pt-2 flex justify-start w-full">
                      <ButtonEl
                        key={i}
                        link={{ target: '_blank', url: cardItem.listingLink }}
                        theme={'text'}
                        className="!text-sm !text-(--color-primary-text) !font-normal !m-0 !p-0 !text-left"
                        onClick={handleCardClick}
                      >
                        {cardItem.dynamicCardLinkText || 'Learn more'}
                        <ArrowRightIcon role="none" className="h-4 w-4 ml-1 shrink-0" />
                      </ButtonEl>
                    </div>
                  </div>
                )
              }

              // ── News Variant ──────────────────────────────────────────────────────
              // Image-less news card mirroring CardListCurated's News layout: meta badge
              // (category + read time + lock) with the publication date on the right, a
              // linked blue title, description, and a member-aware "continue reading" CTA.
              if (variant === 'news') {
                const badgeData = cardItem?.metaBadgeData || {}
                const firstNewsTag = badgeData?.categoryTags?.[0]
                // Events show their event date; other content shows its publication date.
                // dateTimeFormatted already encodes eventDate-then-listingCreatedDate precedence, so
                // it's the authoritative card date — metaBadgeData.publicationDate is always
                // listingCreatedDate and would wrongly override an event's date (NDS-236). For
                // non-events both resolve to the same day; formatDate normalises display to "Mon D, YYYY".
                const newsDate = cardItem.eventDate
                  ? formatDate(cardItem.dateTimeFormatted)
                  : badgeData?.publicationDate || formatDate(cardItem.dateTimeFormatted)
                const newsLocked = !!badgeData?.isLocked
                // null (unknown) is treated as signed-out, matching CardListCurated's resolveCTALabel.
                const showSignIn = newsLocked && isMember !== true
                const ctaLabel = showSignIn
                  ? 'Sign in to continue reading'
                  : cardItem.dynamicCardLinkText || 'Continue reading'

                const handleNewsClick = (e) => {
                  dataLayerPush(
                    showSignIn
                      ? { event: 'login_intent', click_text: stripHtml(cardItem.listingTitle), member_only: true }
                      : {
                          event: 'article_click',
                          click_text: stripHtml(cardItem.listingTitle),
                          category: badgeData.categoryTags?.length ? badgeData.categoryTags.join(', ') : undefined,
                          member_only: false,
                        },
                    e.currentTarget,
                  )
                }

                return (
                  <div key={i} className="relative w-full overflow-hidden p-6">
                    <div className="relative flex flex-col">
                      {(firstNewsTag || badgeData?.readTime || newsDate) && (
                        <div className="flex flex-row items-center gap-4 w-full mb-4">
                          <div className="flex-1 min-w-0">
                            {(firstNewsTag || badgeData?.readTime) && (
                              <MetaBadge
                                primaryText={firstNewsTag}
                                secondaryText={badgeData?.readTime}
                                isLocked={newsLocked}
                                icon={newsLocked ? <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" /> : null}
                                tagType={newsLocked ? 'locked_news_articles' : 'unlocked_news_articles'}
                              />
                            )}
                          </div>
                          {newsDate && (
                            <span className="text-xs font-normal text-(--sb-common-base-body-text-color) shrink-0 whitespace-nowrap">
                              {newsDate}
                            </span>
                          )}
                        </div>
                      )}

                      <div>
                        {cardItem.listingTitle && (
                          <HeroTitle
                            type="dynamic"
                            cardItem={cardItem}
                            idx={i}
                            titleClass="text-3xl leading-8 md:text-4xl md:leading-10"
                            className="text-(--color-primary-text)"
                            onCardClick={handleNewsClick}
                          />
                        )}
                        {cardItem.listingDescription && (
                          <div
                            className="mt-4 !text-(--sb-common-base-body-text-color)"
                            data-rte="true"
                            dangerouslySetInnerHTML={createMarkup(cardItem.listingDescription)}
                          />
                        )}
                      </div>

                      {cardItem.listingLink && (
                        <div className="mt-4">
                          <ButtonEl
                            link={{ url: cardItem.listingLink }}
                            theme="text"
                            className="!text-sm !text-(--color-primary-text) !font-normal !m-0 !p-0 !text-left"
                            onClick={handleNewsClick}
                          >
                            {ctaLabel}
                            <ArrowRightIcon role="none" className="h-4 w-4 ml-1 shrink-0" />
                          </ButtonEl>
                        </div>
                      )}
                    </div>
                  </div>
                )
              }

              // ── Hero Image Variant ────────────────────────────────────────────────
              // Card container stays a <div>; HeroTitle handles title-level linking.
              if (variant === 'hero') {
                return (
                  <div
                    key={i}
                    className={`relative w-full overflow-hidden ${gridCenteringClasses} border-1 border-(--color-border-tertiary) p-6`}
                  >
                    <div className="relative flex flex-col">
                      {showImage && (
                        <Picture
                          desktopImage={cardItem.listingDesktopImage}
                          mobileImage={cardItem.listingMobileImage}
                          className="mx-auto mb-8 aspect-video"
                        />
                      )}
                      <div className="col-start-2 col-span-7 md:col-span-3 lg:col-span-5">
                        {cardItem.dateTimeFormatted && (
                          <span className="text-lg font-normal leading-7 block">{cardItem.dateTimeFormatted}</span>
                        )}
                        {cardItem.listingTitle && (
                          <HeroTitle
                            type="dynamic"
                            cardItem={cardItem}
                            idx={i}
                            titleClass="text-3xl leading-8 md:text-4xl md:leading-10"
                            onCardClick={handleCardClick}
                          />
                        )}
                        {tags.length != 0 && <small className="block">{tags.join(', ')}</small>}
                        {cardItem.listingDescription && (
                          <div
                            className="mt-4 text-lg font-normal leading-7"
                            data-rte="true"
                            dangerouslySetInnerHTML={createMarkup(cardItem.listingDescription)}
                          />
                        )}
                      </div>
                      {cardCtaItems.length != 0 && (
                        <div className="flex flex-col md:flex-row gap-6 mt-4 md:mt-8">
                          {cardCtaItems.map((ctaItem, j) => (
                            <ButtonEl
                              key={j}
                              link={ctaItem.link}
                              theme={ctaItem.linkType.toLowerCase()}
                              icon={ctaItem.linkIconClass}
                              position={ctaItem.linkPosition}
                              onClick={handleCardClick}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              }

              // ── Background Image Variant ──────────────────────────────────────────
              // Full-card <a> when listingLink exists; per-card CTAs get tabIndex={-1}
              // when the card is already a link to avoid double tab stops.
              // dynamicCardLinkText is kept as a visual-only fallback for backward compat.
              const bgStyle = {
                '--mobile-bg-url':
                  showImage && cardItem.listingMobileImage ? `url(${cardItem.listingMobileImage.src})` : null,
                '--desktop-bg-url':
                  showImage && cardItem.listingDesktopImage ? `url(${cardItem.listingDesktopImage.src})` : null,
                '--bg-color': backgroundColor || 'transparent',
                '--light-or-dark': lightOrDarkValue,
                '--text-color':
                  lightOrDarkValue === 'dark' || cardItem.listingDesktopImage || cardItem.listingMobileImage
                    ? 'var(--color-light)'
                    : 'var(--color-dark)',
              }
              const bgClass = `relative w-full pt-4 px-8 pb-8 ${gridCenteringClasses} bg-(--bg-color) text-(--text-color) ${lightOrDarkValue === 'light' ? 'border-1 border-(--color-border-tertiary)' : ''} h-full overflow-hidden ${addBgImageClass(cardItem.listingDesktopImage?.src, cardItem.listingMobileImage?.src)}`

              const bgInner = (
                <>
                  {props.imageGradientColour &&
                    createGradientElement({
                      type: props.imageGradientType,
                      fromColor: gradientColor,
                    })}
                  <div
                    data-type="bg"
                    data-bg={getSectionTheme(backgroundColor)}
                    className="relative min-h-96 h-full mt-auto flex flex-col justify-end"
                  >
                    <div>
                      {cardItem.dateTimeFormatted && (
                        <span className="text-lg font-normal leading-7">{cardItem.dateTimeFormatted}</span>
                      )}
                      {tags.length != 0 && <small className="block">{tags.join(', ')}</small>}
                      {cardItem.listingTitle && !cardItem.useSmallTitle && (
                        <h2
                          className={`block font-bold text-3xl leading-8 md:text-4xl md:leading-10 ${cardItem.titleAlignment ? `text-${cardItem.titleAlignment.toLowerCase()}` : ''}`}
                        >
                          {cardItem.listingTitle}
                        </h2>
                      )}
                      {cardItem.listingTitle && cardItem.useSmallTitle && (
                        <h3
                          className={`block font-bold text-lg ${cardItem.titleAlignment ? `text-${cardItem.titleAlignment.toLowerCase()}` : ''}`}
                        >
                          {cardItem.listingTitle}
                        </h3>
                      )}
                      {cardItem.listingDescription && (
                        <div
                          className="mt-4 md:mt-8 text-lg font-normal leading-7"
                          data-rte="true"
                          dangerouslySetInnerHTML={createMarkup(cardItem.listingDescription)}
                        />
                      )}
                      {cardCtaItems.length != 0 && (
                        <div
                          className={`flex flex-col md:flex-row items-${textAlignment.flex === 'around' ? 'start' : textAlignment.flex} gap-6 mt-10`}
                        >
                          {cardCtaItems.map((ctaItem, j) => (
                            <ButtonEl
                              key={j}
                              link={ctaItem.link}
                              theme={ctaItem.linkType.toLowerCase()}
                              icon={ctaItem.linkIconClass}
                              position={ctaItem.linkPosition}
                              tabIndex={cardItem.listingLink ? -1 : 0}
                              onClick={!cardItem.listingLink ? handleCardClick : undefined}
                            />
                          ))}
                        </div>
                      )}
                      {!cardCtaItems.length && cardItem.dynamicCardLinkText && (
                        <div className={`mt-2 flex justify-${alignment !== 'center' ? 'start' : 'center'}`}>
                          <ButtonEl tabIndex={-1}>{cardItem.dynamicCardLinkText}</ButtonEl>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )

              return cardItem.listingLink ? (
                <a
                  href={cardItem.listingLink}
                  key={i}
                  style={bgStyle}
                  className={bgClass}
                  data-bg={getSectionTheme(backgroundColor)}
                  onClick={handleCardClick}
                >
                  {bgInner}
                </a>
              ) : (
                <div key={i} style={bgStyle} className={bgClass} data-bg={getSectionTheme(backgroundColor)}>
                  {bgInner}
                </div>
              )
            })}
          </div>
        )}

        {/* In-place "View more" expansion only. When a CTA link is configured, the
            top-right section CTA already links there, so we omit the duplicate here. */}
        {!viewMore && !listingUrl && (
          <div className="flex justify-center mt-20">
            <ButtonEl
              onClick={() => {
                setViewMore(true)
              }}
            >
              {props.viewMoreText || 'View more'}
              <ArrowRightIcon role="none" className="h-6 w-6 ml-2" />
            </ButtonEl>
          </div>
        )}
      </div>
    </Section>
  )
}

export default CardListDynamic
